import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, AlertTriangle, Loader, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | valid | error | done
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/password-reset/${token}`)
      .then(() => setStatus('valid'))
      .catch((err) => {
        setErrorMsg(err.response?.data?.detail || 'This reset link is invalid or has expired.');
        setStatus('error');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/password-reset`, { token, new_password: password });
      setStatus('done');
      toast.success('Password reset! You can now log in.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl border border-slate-200" data-testid="reset-password-page">
        {status === 'loading' && (
          <div className="text-center py-8">
            <Loader className="animate-spin mx-auto text-indigo-500 mb-4" size={32} />
            <p className="text-slate-600">Verifying reset link…</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-8" data-testid="reset-password-error-state">
            <AlertTriangle size={40} className="mx-auto text-rose-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid</h2>
            <p className="text-slate-600 mb-6">{errorMsg}</p>
            <Link to="/forgot-password">
              <Button variant="outline" data-testid="request-new-link-button">Request a New Link</Button>
            </Link>
          </div>
        )}

        {status === 'done' && (
          <div className="text-center py-8" data-testid="reset-password-done-state">
            <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Password reset!</h2>
            <p className="text-slate-600 mb-6">You can now log in with your new password.</p>
            <Button onClick={() => navigate('/login')} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="go-to-login-button">
              Go to Login
            </Button>
          </div>
        )}

        {status === 'valid' && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound size={24} className="text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Set a new password</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-password">New Password *</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm Password *</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                disabled={submitting}
                data-testid="reset-password-submit-button"
              >
                {submitting ? 'Resetting…' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
